import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextHeadline, TextTitle1, TextTitle3, TextLabel2 } from '@cbhq/cds-web/typography';
import { memo, useMemo } from 'react';
import { DisplayType, format } from '@/utils/numberFormatter';
import { useTokenizedEquitiesData } from './hooks/useTokenizedEquitiesData';

const SUMMARY_CARD_TITLE = 'Tokens Overview';
const SUMMARY_ITEMS_GAP = 3;

const SUMMARY_LABELS = ['Tokenized Market Cap on Coinbase', 'Tokenized Trade Volume on Coinbase'];

type SummaryItem = {
  label: string;
  value: string;
  change?: number;
};

function TokenizedEquitiesSummaryItemsLoading() {
  return (
    <>
      {SUMMARY_LABELS.map((label) => (
        <VStack key={label} gap={0.5} flexGrow={1}>
          <TextHeadline as="span" noWrap color="fgMuted">
            {label}
          </TextHeadline>
          <Fallback width="80%" height={36} />
        </VStack>
      ))}
    </>
  );
}

function TokenizedEquitiesSummaryItems() {
  const { summary, isLoading } = useTokenizedEquitiesData();

  const summaryItems: SummaryItem[] = useMemo(
    () => [
      {
        label: SUMMARY_LABELS[0],
        value: format({ value: summary.marketCap, type: DisplayType.FiatShort }),
        change: summary.marketCapChange,
      },
      {
        label: SUMMARY_LABELS[1],
        value: format({ value: summary.tradeVolume, type: DisplayType.FiatShort }),
        change: summary.tradeVolumeChange,
      },
    ],
    [summary],
  );

  if (isLoading) {
    return <TokenizedEquitiesSummaryItemsLoading />;
  }

  return (
    <>
      {summaryItems.map((item) => {
        const isPositive = (item.change ?? 0) > 0;
        const changeColor = isPositive ? 'fgPositive' : 'fgNegative';
        const changeIcon = isPositive ? '↗' : '↘';

        return (
          <VStack key={item.label} gap={0.5} flexGrow={1}>
            <TextHeadline as="span" noWrap color="fgMuted">
              {item.label}
            </TextHeadline>
            <HStack gap={1} alignItems="center">
              <TextTitle1 as="span">{item.value}</TextTitle1>
              {item.change !== undefined && item.change !== 0 && (
                <TextLabel2 as="span" color={changeColor}>
                  {changeIcon} {Math.abs(item.change)}%
                </TextLabel2>
              )}
            </HStack>
          </VStack>
        );
      })}
    </>
  );
}

const TokenizedEquitiesSummaryContent = memo(function TokenizedEquitiesSummaryContent() {
  return (
    <VStack gap={3}>
      {/* Title */}
      <TextTitle3 as="h1" testID="tokenized-equities-summary-title">
        {SUMMARY_CARD_TITLE}
      </TextTitle3>

      {/* Summary Items */}
      <VStack gap={SUMMARY_ITEMS_GAP}>
        <TokenizedEquitiesSummaryItems />
      </VStack>
    </VStack>
  );
});

export const TokenizedEquitiesSummary = memo(function TokenizedEquitiesSummary() {
  return (
    <VStack background="bg" borderRadius={300} padding={3}>
      <TokenizedEquitiesSummaryContent />
    </VStack>
  );
});
